import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Megaphone, Trash2, Pencil, X } from "lucide-react";
import { BANNER_THEMES } from "../../components/SiteBanner";

const EMPTY = {
  message: "",
  link: "",
  theme: "ink",
  active: true,
  dismissible: true,
  starts_on: "",
  ends_on: "",
};

const THEME_LABELS = { ink: "Taupe", rose: "Rose poudré", sable: "Sable" };

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR");
}

function periodLabel(b) {
  const from = fmtDate(b.starts_on);
  const to = fmtDate(b.ends_on);
  if (from && to) return `du ${from} au ${to}`;
  if (from) return `à partir du ${from}`;
  if (to) return `jusqu'au ${to}`;
  return "en permanence";
}

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/banners")
      .then(r => setBanners(r.data.banners || []))
      .catch(() => toast.error("Erreur de chargement"));

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const edit = (b) => {
    setEditingId(b.id);
    setForm({
      message: b.message || "",
      link: b.link || "",
      theme: b.theme || "ink",
      active: b.active !== false,
      dismissible: b.dismissible !== false,
      starts_on: b.starts_on || "",
      ends_on: b.ends_on || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        message: form.message.trim(),
        link: form.link.trim() || null,
        theme: form.theme,
        active: form.active,
        dismissible: form.dismissible,
        starts_on: form.starts_on || null,
        ends_on: form.ends_on || null,
      };
      if (editingId) {
        await api.put(`/admin/banners/${editingId}`, payload);
        toast.success("Bannière mise à jour ✿");
      } else {
        await api.post("/admin/banners", payload);
        toast.success("Bannière créée ✿");
      }
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/admin/banners/${b.id}`, {
        message: b.message,
        link: b.link || null,
        theme: b.theme,
        active: !b.active,
        dismissible: b.dismissible !== false,
        starts_on: b.starts_on || null,
        ends_on: b.ends_on || null,
      });
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer la bannière « ${b.message} » ?`)) return;
    try {
      await api.delete(`/admin/banners/${b.id}`);
      if (editingId === b.id) reset();
      toast.success("Bannière supprimée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <p className="overline text-berulia-rosedeep">BERULIA</p>
      <h1 className="font-heading text-3xl sm:text-4xl mt-2 mb-8">Bannières</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Création / édition */}
        <form onSubmit={submit} data-testid="banner-form" className="lg:col-span-1 bg-white border border-berulia-border p-6 space-y-4 self-start">
          <p className="overline flex items-center gap-2">
            <Megaphone size={14}/> {editingId ? "Modifier la bannière" : "Nouvelle bannière"}
          </p>

          <label className="block text-sm">
            <span className="text-berulia-inkmuted">Message</span>
            <input
              value={form.message}
              onChange={e => set("message", e.target.value)}
              required
              maxLength={200}
              placeholder="☀️ Soldes d'été : jusqu'à -50%"
              data-testid="banner-message"
              className="mt-1 w-full border border-berulia-border px-3 py-2"
            />
          </label>

          <label className="block text-sm">
            <span className="text-berulia-inkmuted">Lien (optionnel)</span>
            <input
              value={form.link}
              onChange={e => set("link", e.target.value)}
              placeholder="/boutique"
              className="mt-1 w-full border border-berulia-border px-3 py-2"
            />
          </label>

          <label className="block text-sm">
            <span className="text-berulia-inkmuted">Couleur</span>
            <select
              value={form.theme}
              onChange={e => set("theme", e.target.value)}
              className="mt-1 w-full border border-berulia-border px-3 py-2 bg-white"
            >
              {Object.keys(THEME_LABELS).map(t => <option key={t} value={t}>{THEME_LABELS[t]}</option>)}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-berulia-inkmuted">Début</span>
              <input type="date" value={form.starts_on} onChange={e => set("starts_on", e.target.value)}
                className="mt-1 w-full border border-berulia-border px-3 py-2"/>
            </label>
            <label className="block text-sm">
              <span className="text-berulia-inkmuted">Fin</span>
              <input type="date" value={form.ends_on} onChange={e => set("ends_on", e.target.value)}
                className="mt-1 w-full border border-berulia-border px-3 py-2"/>
            </label>
          </div>
          <p className="text-xs text-berulia-inkmuted">Dates incluses. Laisser vide pour une diffusion permanente.</p>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)}/>
            Activer
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.dismissible} onChange={e => set("dismissible", e.target.checked)}/>
            La cliente peut la fermer
          </label>

          {/* Aperçu */}
          <div>
            <p className="text-xs text-berulia-inkmuted mb-1">Aperçu</p>
            <div className={`text-center px-4 py-2 text-xs tracking-wide ${BANNER_THEMES[form.theme]}`}>
              {form.message || "Votre message ici"}
            </div>
          </div>

          <div className="flex gap-2">
            <button disabled={saving} data-testid="banner-submit"
              className="flex-1 bg-berulia-ink text-white text-sm py-2.5 disabled:opacity-50">
              {saving ? "…" : editingId ? "Enregistrer" : "Créer la bannière"}
            </button>
            {editingId && (
              <button type="button" onClick={reset} className="px-3 border border-berulia-border text-sm">
                <X size={16}/>
              </button>
            )}
          </div>
        </form>

        {/* Liste */}
        <div className="lg:col-span-2 bg-white border border-berulia-border">
          {banners.length === 0 ? (
            <p className="p-6 text-sm text-berulia-inkmuted">Aucune bannière pour le moment.</p>
          ) : (
            <ul className="divide-y divide-berulia-border">
              {banners.map(b => (
                <li key={b.id} data-testid="banner-row" className="p-4 sm:p-5 flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className={`inline-block px-3 py-1 text-xs ${BANNER_THEMES[b.theme] || BANNER_THEMES.ink}`}>
                      {b.message}
                    </div>
                    <p className="text-xs text-berulia-inkmuted mt-2">
                      {periodLabel(b)}
                      {b.link ? ` · lien ${b.link}` : ""}
                      {b.dismissible === false ? " · non fermable" : ""}
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${b.live ? "bg-berulia-mint text-berulia-inkdeep" : "bg-berulia-beigewarm text-berulia-inkmuted"}`}>
                    {b.live ? "En ligne" : b.active ? "Programmée" : "Inactive"}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => toggleActive(b)} title={b.active ? "Désactiver" : "Activer"}
                      className="px-2 py-1 text-xs border border-berulia-border text-berulia-inkmuted hover:text-berulia-ink">
                      {b.active ? "Désactiver" : "Activer"}
                    </button>
                    <button onClick={() => edit(b)} aria-label="Modifier"
                      className="p-1.5 border border-berulia-border text-berulia-inkmuted hover:text-berulia-ink">
                      <Pencil size={14}/>
                    </button>
                    <button onClick={() => remove(b)} aria-label="Supprimer"
                      className="p-1.5 border border-berulia-border text-berulia-inkmuted hover:text-red-600">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
